import React, { useState, useEffect, useCallback } from 'react';

import { Button } from './ui/button';
import { Input } from './ui/input';

import { Tabs, TabsList, TabsTrigger } from './ui/tabs';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

import { Package, Plus, Users, CreditCard, Wallet, Coins, TrendingUp, Shield, Building, Briefcase, Eye, UserPlus, Search, BarChart3, Zap, Star, Calendar, CheckCircle2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

import { getToken } from '../services/tokenService';
import InstitutionalHero from './ui/InstitutionalHero';
import GestionPlansPlansTab from './GestionPlans_PlansTab';
import GestionPlansAssignmentsTab from './GestionPlans_AssignmentsTab';
import GestionPlansAnalyticsTab from './GestionPlans_AnalyticsTab';
import GestionPlansPlanDialog from './GestionPlans_PlanDialog';
import GestionPlansAssignDialog from './GestionPlans_AssignDialog';
import GestionPlansDetailDialog from './GestionPlans_DetailDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Types de tarification avec icônes et couleurs FONCÉES
const PRICING_TYPES = [
  { value: 'classique', label: 'Abonnement Classique', icon: CreditCard, color: 'blue', gradient: 'from-blue-500 to-blue-600', bg: 'bg-blue-600', text: 'text-white', description: 'Forfait mensuel ou annuel fixe' },
  { value: 'usage', label: 'Tarification à l\'Usage', icon: Zap, color: 'orange', gradient: 'from-orange-500 to-amber-500', bg: 'bg-orange-600', text: 'text-white', description: 'Paiement selon la consommation' },
  { value: 'mixte', label: 'Tarification Mixte', icon: TrendingUp, color: 'purple', gradient: 'from-purple-500 to-pink-500', bg: 'bg-purple-600', text: 'text-white', description: 'Forfait de base + usage variable' },
  { value: 'credits', label: 'Système de Crédits', icon: Coins, color: 'yellow', gradient: 'from-yellow-500 to-amber-500', bg: 'bg-yellow-600', text: 'text-white', description: 'Achat de crédits pré-payés' },
  { value: 'wallet', label: 'Wallet / Porte-monnaie', icon: Wallet, color: 'green', gradient: 'from-emerald-500 to-green-500', bg: 'bg-emerald-600', text: 'text-white', description: 'Solde rechargeable à volonté' },
  { value: 'echelonne', label: 'Paiement Échelonné', icon: Calendar, color: 'rose', gradient: 'from-rose-500 to-pink-600', bg: 'bg-rose-600', text: 'text-white', description: 'Acompte + échéances (sans taux)' }
];

// Rôles cibles avec couleurs et descriptions
const TARGET_ROLES = [
  { value: 'membre', label: 'Membres', icon: Users, color: 'emerald', bg: 'bg-emerald-600', text: 'text-white' },
  { value: 'cooper', label: "COOPER'S", icon: Building, color: 'purple', bg: 'bg-purple-600', text: 'text-white' },
  { value: 'expert', label: 'Experts', icon: Briefcase, color: 'orange', bg: 'bg-orange-600', text: 'text-white' },
  { value: 'visiteur', label: 'Visiteurs', icon: Eye, color: 'gray', bg: 'bg-gray-600', text: 'text-white' },
  { value: 'partenaire', label: 'Partenaires', icon: Shield, color: 'blue', bg: 'bg-blue-600', text: 'text-white' },
  { value: 'all', label: 'Tous', icon: Star, color: 'yellow', bg: 'bg-yellow-600', text: 'text-white' }
];

const DEFAULT_PLAN = {
  nom: '',
  description: '',
  type_tarification: 'classique',
  pricing: {
    type: 'classique',
    prix_mensuel: 0,
    prix_annuel: 0,
    prix_unitaire: 0,
    credits_inclus: 0,
    wallet_minimum: 0,
    devise: 'EUR',
    taxes_personnalisees: []
  },
  roles_cibles: ['membre'],
  fonctionnalites: [],
  is_active: true,
  is_default: false,
  ordre_affichage: 0,
  couleur: '#8B5CF6',
  icone: 'Package'
};

// Couleurs prédéfinies pour les plans
const PLAN_COLORS = [
  { value: '#8B5CF6', label: 'Violet', class: 'bg-purple-500' },
  { value: '#3B82F6', label: 'Bleu', class: 'bg-blue-500' },
  { value: '#10B981', label: 'Vert', class: 'bg-emerald-500' },
  { value: '#F59E0B', label: 'Orange', class: 'bg-amber-500' },
  { value: '#EF4444', label: 'Rouge', class: 'bg-red-500' },
  { value: '#EC4899', label: 'Rose', class: 'bg-pink-500' },
  { value: '#06B6D4', label: 'Cyan', class: 'bg-cyan-500' },
  { value: '#6366F1', label: 'Indigo', class: 'bg-indigo-500' }
];

// Fonction utilitaire pour extraire le message d'erreur (gère Pydantic et autres formats)
const getErrorMessage = (error, defaultMsg = 'Une erreur est survenue') => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || 'Erreur de validation').join(', ');
  }
  return error.message || defaultMsg;
};

export default function GestionPlans() {
  const [activeTab, setActiveTab] = useState('plans');
  const [plans, setPlans] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterType, setFilterType] = useState('all');
  
  // Dialog states
  const [showPlanDialog, setShowPlanDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [planForm, setPlanForm] = useState(DEFAULT_PLAN);
  const [assignForm, setAssignForm] = useState({ user_id: '', plan_id: '' });
  const [saving, setSaving] = useState(false);
  const [dialogStep, setDialogStep] = useState(1);

  // Sélection en masse de plans
  const [selectedPlanIds, setSelectedPlanIds] = useState(new Set());

  // Recherche dynamique dans modale Assigner
  const [assignUserSearch, setAssignUserSearch] = useState('');
  const [assignPlanSearch, setAssignPlanSearch] = useState('');

  // Filtres / recherche onglet Assignations
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [assignmentStatusFilter, setAssignmentStatusFilter] = useState('all');

  const getAuthHeader = () => ({
    Authorization: `Bearer ${getToken()}`
  });

  // Charger les plans
  const fetchPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/plans/`, {
        headers: getAuthHeader()
      });
      setPlans(response.data.plans || []);
    } catch {
      toast.error('Erreur lors du chargement des plans');
    }
  }, []);

  // Charger les assignations
  const fetchAssignments = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/plans/assignments/`, {
        headers: getAuthHeader()
      });
      setAssignments(response.data.assignments || []);
    } catch {
    }
  }, []);

  // Charger les utilisateurs
  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/invitations/users/list`, {
        headers: getAuthHeader()
      });
      setUsers(response.data.users || []);
    } catch {
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPlans(), fetchAssignments(), fetchUsers()]);
      setLoading(false);
    };
    loadData();
  }, [fetchPlans, fetchAssignments, fetchUsers]);

  // Créer/Modifier un plan
  const handleSavePlan = async () => {
    if (!planForm.nom) {
      toast.error('Le nom du plan est requis');
      return;
    }

    try {
      setSaving(true);
      
      const payload = {
        ...planForm,
        pricing: {
          ...planForm.pricing,
          type: planForm.type_tarification
        }
      };

      if (editingPlan) {
        await axios.put(`${BACKEND_URL}/api/plans/${editingPlan.id}`, payload, {
          headers: getAuthHeader()
        });
        toast.success('Plan mis à jour avec succès');
      } else {
        await axios.post(`${BACKEND_URL}/api/plans/`, payload, {
          headers: getAuthHeader()
        });
        toast.success('Nouveau plan créé avec succès');
      }

      setShowPlanDialog(false);
      setPlanForm(DEFAULT_PLAN);
      setEditingPlan(null);
      setDialogStep(1);
      fetchPlans();
    } catch (error) {
      // Gérer les erreurs Pydantic (tableau d'objets) et les erreurs simples (string)
      toast.error(getErrorMessage(error, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  // Supprimer un plan
  const handleDeletePlan = async (plan) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le plan "${plan.nom}" ?`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/plans/${plan.id}`, {
        headers: getAuthHeader()
      });
      toast.success('Plan supprimé avec succès');
      fetchPlans();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la suppression'));
    }
  };

  // Dupliquer un plan
  const handleDuplicatePlan = async (plan, openEditAfter = false) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/plans/${plan.id}/duplicate`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Plan dupliqué avec succès');
      await fetchPlans();
      if (openEditAfter && res.data?.plan) {
        // Ouvrir le dialog d'édition immédiatement pour personnaliser la copie
        setEditingPlan(res.data.plan);
        setPlanForm({ ...res.data.plan, pricing: res.data.plan.pricing || DEFAULT_PLAN.pricing });
        setDialogStep(1);
        setShowPlanDialog(true);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la duplication'));
    }
  };

  // Suppression en masse de plans
  const handleBulkDeletePlans = async () => {
    if (selectedPlanIds.size === 0) return;
    if (!window.confirm(`Supprimer définitivement ${selectedPlanIds.size} plan(s) sélectionné(s) ?\n(Les plans avec des abonnés actifs seront ignorés)`)) return;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/plans/bulk-delete`,
        { plan_ids: Array.from(selectedPlanIds) },
        { headers: getAuthHeader() }
      );
      const { deleted_count = 0, skipped_count = 0, skipped = [] } = res.data || {};
      toast.success(`${deleted_count} plan(s) supprimé(s)${skipped_count > 0 ? ` · ${skipped_count} ignoré(s)` : ''}`);
      if (skipped.length > 0) {
        skipped.forEach((s) => toast.info(`${s.nom || s.id}: ${s.reason}`));
      }
      setSelectedPlanIds(new Set());
      fetchPlans();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la suppression en masse'));
    }
  };

  // Activer/Désactiver en masse (préserve les abonnés)
  const handleBulkToggleActive = async (is_active) => {
    if (selectedPlanIds.size === 0) return;
    const verb = is_active ? 'Activer' : 'Désactiver';
    if (!window.confirm(`${verb} ${selectedPlanIds.size} plan(s) sélectionné(s) ?\n(Les abonnés actifs sont préservés)`)) return;
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/plans/bulk-toggle`,
        { plan_ids: Array.from(selectedPlanIds), is_active },
        { headers: getAuthHeader() }
      );
      const modified = res.data?.modified_count ?? 0;
      toast.success(`${modified} plan(s) ${is_active ? 'activé(s)' : 'désactivé(s)'}`);
      setSelectedPlanIds(new Set());
      fetchPlans();
    } catch (error) {
      toast.error(getErrorMessage(error, `Erreur lors de la ${verb.toLowerCase()}ion en masse`));
    }
  };

  const togglePlanSelection = (planId) => {
    setSelectedPlanIds((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) next.delete(planId);
      else next.add(planId);
      return next;
    });
  };

  const toggleSelectAllPlans = () => {
    const ids = filteredPlans.map((p) => p.id);
    const allSelected = ids.every((id) => selectedPlanIds.has(id));
    setSelectedPlanIds(allSelected ? new Set() : new Set(ids));
  };

  // Assigner un plan
  const handleAssignPlan = async () => {
    if (!assignForm.user_id || !assignForm.plan_id) {
      toast.error('Veuillez sélectionner un utilisateur et un plan');
      return;
    }

    try {
      setSaving(true);
      await axios.post(`${BACKEND_URL}/api/plans/assign`, assignForm, {
        headers: getAuthHeader()
      });
      toast.success('Plan assigné avec succès');
      setShowAssignDialog(false);
      setAssignForm({ user_id: '', plan_id: '' });
      fetchAssignments();
      fetchPlans();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de l\'assignation'));
    } finally {
      setSaving(false);
    }
  };

  // Ouvrir le dialog d'édition
  const openEditDialog = (plan) => {
    setEditingPlan(plan);
    setPlanForm({
      ...plan,
      pricing: plan.pricing || DEFAULT_PLAN.pricing
    });
    setDialogStep(1);
    setShowPlanDialog(true);
  };

  // Ouvrir le dialog de création
  const openCreateDialog = () => {
    setEditingPlan(null);
    setPlanForm(DEFAULT_PLAN);
    setDialogStep(1);
    setShowPlanDialog(true);
  };

  // Voir les détails d'un plan
  const openDetailDialog = (plan) => {
    setSelectedPlan(plan);
    setShowDetailDialog(true);
  };

  const getPricingTypeConfig = (type) => PRICING_TYPES.find(t => t.value === type) || PRICING_TYPES[0];
  const getRoleConfig = (role) => TARGET_ROLES.find(r => r.value === role) || TARGET_ROLES[0];

  // Filtres
  const filteredPlans = plans.filter(plan => {
    const matchSearch = !searchTerm || 
      plan.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      plan.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchRole = filterRole === 'all' || plan.roles_cibles?.includes(filterRole);
    const matchType = filterType === 'all' || plan.type_tarification === filterType;
    return matchSearch && matchRole && matchType;
  });

  // Statistiques améliorées
  const stats = {
    totalPlans: plans.length,
    activePlans: plans.filter(p => p.is_active).length,
    inactivePlans: plans.filter(p => !p.is_active).length,
    totalSubscribers: plans.reduce((acc, p) => acc + (p.subscribers_count || 0), 0),
    activeAssignments: assignments.filter(a => a.statut === 'actif').length,
    byType: PRICING_TYPES.map(t => ({
      ...t,
      count: plans.filter(p => p.type_tarification === t.value).length
    })),
    monthlyRevenue: plans.reduce((acc, p) => {
      const subs = p.subscribers_count || 0;
      const price = p.pricing?.prix_mensuel || 0;
      return acc + (subs * price);
    }, 0)
  };

  // Rendu prix du plan
  const renderPlanPrice = (plan) => {
    const pricing = plan.pricing || {};
    
    if (pricing.prix_mensuel > 0) {
      return (
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold">{pricing.prix_mensuel}</span>
          <span className="text-lg text-gray-500">€</span>
          <span className="text-sm text-gray-400">/mois</span>
        </div>
      );
    }
    if (pricing.prix_unitaire > 0) {
      return (
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold">{pricing.prix_unitaire}</span>
          <span className="text-lg text-gray-500">€</span>
          <span className="text-sm text-gray-400">/unité</span>
        </div>
      );
    }
    if (pricing.credits_inclus > 0) {
      return (
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-bold">{pricing.credits_inclus}</span>
          <span className="text-sm text-gray-400">crédits</span>
        </div>
      );
    }
    return <span className="text-2xl font-bold text-gray-400">Gratuit</span>;
  };

  // ctx-bag : état + handlers partagés avec les sous-composants extraits
  const ctx = {
    activeTab, assignForm, assignPlanSearch, assignUserSearch, assignmentSearch,
    assignmentStatusFilter, assignments, dialogStep, editingPlan, filterRole, filterType,
    loading, planForm, plans, saving, searchTerm, selectedPlan, selectedPlanIds,
    showAssignDialog, showDetailDialog, showPlanDialog, users,
    setActiveTab, setAssignForm, setAssignPlanSearch, setAssignUserSearch, setAssignmentSearch,
    setAssignmentStatusFilter, setAssignments, setDialogStep, setEditingPlan, setFilterRole,
    setFilterType, setLoading, setPlanForm, setPlans, setSaving, setSearchTerm, setSelectedPlan,
    setSelectedPlanIds, setShowAssignDialog, setShowDetailDialog, setShowPlanDialog, setUsers,
    fetchAssignments, fetchPlans, fetchUsers, filteredPlans, getAuthHeader, getPricingTypeConfig,
    getRoleConfig, handleAssignPlan, handleBulkDeletePlans, handleBulkToggleActive, handleDeletePlan,
    handleDuplicatePlan, handleSavePlan, openCreateDialog, openDetailDialog, openEditDialog,
    renderPlanPrice, stats, togglePlanSelection, toggleSelectAllPlans,
    PRICING_TYPES, TARGET_ROLES, DEFAULT_PLAN, PLAN_COLORS, getErrorMessage,
  };

  return (
    <div className="space-y-6">
      {/* Header institutionnel uniforme */}
      <InstitutionalHero
        icon={Package}
        badge="Catalogue d'offres"
        title="Gestion des Plans"
        subtitle="Création, paramétrage et pilotage des plans d'abonnement (forfaits, usage, mixte, crédits, wallet, échelonné) pour COOPER'S, EXPERTS et MEMBRES."
        gradient="bg-gradient-to-br from-slate-900 via-violet-900 to-indigo-900"
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => setShowAssignDialog(true)}
              className="bg-white/10 hover:bg-white/20 text-white border-white/30 backdrop-blur-sm"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Assigner un plan
            </Button>
            <Button
              onClick={openCreateDialog}
              className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-bold shadow-md"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nouveau plan
            </Button>
          </>
        }
        kpis={[
          { label: 'Total plans', value: stats.totalPlans, icon: Package, accent: 'bg-white/15' },
          { label: 'Plans actifs', value: stats.activePlans, icon: CheckCircle2, accent: 'bg-emerald-400/25' },
          { label: 'Abonnés', value: stats.totalSubscribers, icon: Users, accent: 'bg-blue-400/25' },
          { label: 'Revenus/mois', value: `${stats.monthlyRevenue.toLocaleString('fr-FR')}€`, icon: TrendingUp, accent: 'bg-amber-400/25' },
        ]}
      />

      {/* Tabs avec style amélioré */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <TabsList className="bg-white shadow-md rounded-xl p-1.5 border">
            <TabsTrigger value="plans" className="flex items-center gap-2 px-4 py-2.5 rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <Package className="h-4 w-4" />
              Plans ({plans.length})
            </TabsTrigger>
            <TabsTrigger value="assignments" className="flex items-center gap-2 px-4 py-2.5 rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <Users className="h-4 w-4" />
              Assignations ({assignments.length})
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2 px-4 py-2.5 rounded-lg data-[state=active]:bg-purple-600 data-[state=active]:text-white">
              <BarChart3 className="h-4 w-4" />
              Analytique
            </TabsTrigger>
          </TabsList>

          {/* Barre de recherche et filtres */}
          {activeTab === 'plans' && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher un plan..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Type de tarification" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {PRICING_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={() => { fetchPlans(); fetchAssignments(); }}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Tab Plans */}
        <GestionPlansPlansTab ctx={ctx} />
        {/* Tab Assignments */}
        <GestionPlansAssignmentsTab ctx={ctx} />

        {/* Tab Analytics */}
        <GestionPlansAnalyticsTab ctx={ctx} />
      </Tabs>

      {/* Dialog Création/Edition Plan - Design amélioré */}
      <GestionPlansPlanDialog ctx={ctx} />

      {/* Dialog Assignation */}
      <GestionPlansAssignDialog ctx={ctx} />

      {/* Dialog Détails Plan */}
      <GestionPlansDetailDialog ctx={ctx} />
    </div>
  );
}
